import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Steps, Step, Code, Screenshot, PropTable } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is a Cup?', level: 2 as const },
  { id: 'create', title: 'Creating a Cup', level: 2 as const },
  { id: 'traits', title: 'Adding traits', level: 2 as const },
  { id: 'identifier', title: 'Identifier trait', level: 2 as const },
  { id: 'relations', title: 'Relations', level: 2 as const },
  { id: 'fields', title: 'Cup fields', level: 2 as const },
]

export function Cups() {
  return (
    <Article toc={toc}>
      <H1>Cups (Entity Types)</H1>
      <Lead>A Cup is the blueprint for a category of records. Think of it as a database table schema — it defines what fields each record has and how it relates to other entities.</Lead>

      <H2 id="what">What is a Cup?</H2>
      <P>A Cup defines the shape of a <em>Coffee</em> (record). Every coffee in a cup has the same set of available traits (fields), though individual beans (field values) may be empty. Cups are always scoped to a business.</P>
      <Callout type="info">Unlike rigid database tables, cups are schemaless by default. Adding a new trait to a cup does not retroactively require existing coffees to have a value for it.</Callout>

      <H2 id="create">Creating a Cup</H2>
      <Steps>
        <Step n={1} title="Open Cups">Navigate to <strong>Brainer › Cups</strong> and click <strong>New Cup</strong>.</Step>
        <Step n={2} title="Set the title">Enter a clear, singular noun — e.g. <Code>Product</Code>, <Code>Customer</Code>, <Code>Invoice</Code>.</Step>
        <Step n={3} title="Save">The cup is created and you are taken to its detail page.</Step>
      </Steps>
      <Screenshot alt="Cup detail page with the title, traits panel, and relations panel visible" caption="The Cup detail page is where you manage traits and relations." />

      <H2 id="traits">Adding traits</H2>
      <P>Traits define the fields available on each record. They are created in <strong>Brainer › Traits</strong> and then attached to the cup in a specific order.</P>
      <Steps>
        <Step n={1} title="Go to the Cup Traits tab">On the cup detail page, open the <strong>Traits</strong> panel.</Step>
        <Step n={2} title="Click Add Trait">Select a trait from the dropdown and choose its position.</Step>
        <Step n={3} title="Repeat">Add all the fields your records need. Drag to reorder.</Step>
      </Steps>
      <Callout type="tip">You can also use <strong>Collections</strong> to add a group of pre-selected traits all at once — useful for common field sets like an address block or a contact block.</Callout>

      <H2 id="identifier">Identifier trait</H2>
      <P>Mark one trait as the <strong>identifier</strong> for a cup. This trait is used as the human-readable label when the cup appears as a parent relation — for example, in dropdown pickers and in the <Code>identifier</Code> field returned by <Code>findPaginatedResolved()</Code>.</P>
      <Callout type="warning">Only one trait per cup can be marked as the identifier. If no identifier is set, the raw UUID is used as the label in relation displays.</Callout>

      <H2 id="relations">Relations</H2>
      <P>Use the <strong>Relations</strong> panel on the cup detail page to define parent-child links to other cups. Each relation needs:</P>
      <UL>
        <LI><strong>Parent cup</strong> — the cup on the "one" side</LI>
        <LI><strong>Child cup</strong> — the cup on the "many" side (the current cup)</LI>
        <LI><strong>Label</strong> — a name for the relation, e.g. <Code>Client</Code> or <Code>belongs_to</Code></LI>
      </UL>
      <P>See <a href="/docs/brainer/relations" className="text-blue-600 underline underline-offset-2">Relations</a> for a full guide.</P>

      <H2 id="fields">Cup fields</H2>
      <PropTable props={[
        { name: 'title', type: 'string', required: true, description: 'Singular noun identifying this entity type. Used as the result key in findPaginatedResolved().' },
        { name: 'description', type: 'string', description: 'Optional description shown in the builder UI.' },
        { name: 'businessId', type: 'uuid', required: true, description: 'Tenant the cup belongs to. Set automatically.' },
      ]} />
    </Article>
  )
}
